import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    signal,
    untracked,
    viewChild,
    viewChildren,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25InvoiceControlPanelComponent } from "./invoice-control-panel/s25.invoice.control.panel.component";
import { invoiceDisplayName, lineItemLabel, PricingStore, PricingView } from "../s25.event.pricing.store";
import { isDefaultSet } from "./system-sets/system-sets";
import {
    AccountOccurrenceSubtotal,
    AccountSubTotal,
    Invoice,
    LineItem,
    lineItemType,
    Occurrence,
    PricingOccurrence,
} from "../../../../pojo/Pricing";
import { S25Const } from "../../../../util/s25-const";
import { Table } from "../../../s25-table/Table";
import { OccDataMap, S25PricingUtil } from "../../s25.pricing.util";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { S25TriggerRerenderModule } from "../../../s25-trigger-rerender/s25.trigger.rerender.module";
import { Bind } from "../../../../decorators/bind.decorator";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";
import { S25Util } from "../../../../util/s25-util";
import {
    OccurrenceAdjustmentTarget,
    S25PricingAdjustmentCellComponent,
} from "./adjustment-cell/s25.pricing.adjustment.cell.component";
import { GenericTableSimpleSelectComponent } from "../../../s25-table/generics/generic.table.simple.select.component";
import { S25PricingRateGroupDropdown } from "../pricing-rate-group-dropdown/s25.pricing.rate.group.dropdown";
import { PricingColumns } from "../pricing.columns";
import { InvoiceMove } from "./move-item/invoice-move";
import { S25MoveItemAdjustmentsComponent } from "./move-item/s25.move.item.adjustments.component";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25ModalComponent } from "../../../s25-modal/s25.modal.component";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { InvoiceTotals } from "./totals/invoice-totals";
import { S25InvoiceTotalsComponent } from "./totals/s25.invoice.totals.component";
import { S25CollapseChevronComponent } from "../../../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25InvoiceNumberComponent } from "./invoice-number/s25.invoice.number.component";
import { PaymentsStore } from "../payments/s25.payments.store";
import { S25OrgPaymentsComponent } from "../payments/s25.org.payments.component";
import { S25PricingFreshbooksComponent } from "../payments/s25.pricing.freshbooks.component";
import { S25PaymentRequestComponent } from "../payments/s25.payment.request.component";
import { S25PricingHideableItemComponent } from "./hideable-item/s25.pricing.hideable.item.component";

const currency = S25PricingUtil.formatCurrency;

/**
 * `invoice` renders one invoice as itself, editable. `billedItems` renders the reference set for the
 * invoice summary: the same rows and totals, read-only apart from Invoice To.
 */
export type InvoiceViewMode = "invoice" | "billedItems";

type OrgTable = { orgName: string; orgId: number; tableConfig: Table.DataSource };

/** Formatted for an org's header, which reports the same charge its table's total row does. */
type OrgFigures = { totalCharge: string; payments: string; balance: string };

type PendingMoveBase = { item: InvoiceMove.MovableItem; label: string; rowKey: string; source: Invoice };
type PendingMove =
    | ({ kind: "remove" } & PendingMoveBase)
    | ({ kind: "move"; target: Invoice; adjustedItems: LineItem[] } & PendingMoveBase);

/** One line item's slice of a single occurrence, paired with the line item it came from. */
type OccurrenceSlice = { slice: PricingOccurrence; parent: LineItem };

type OccurrenceGroup = {
    rsrvId: number;
    label: string;
    eventId: number | undefined;
    slices: OccurrenceSlice[];
};

// Occurrence view reports occurrence-level amounts on their own, with line item adjustments
// broken out into a separate footer row below the subtotal.
function sumOccurrenceSubtotals(subtotals: AccountSubTotal[]) {
    return subtotals.reduce(
        (totals, subtotal) => ({
            listPrice: totals.listPrice + (subtotal.occurrenceListPrice ?? 0) + (subtotal.requirementsListPrice ?? 0),
            adjustments:
                totals.adjustments + (subtotal.occurrenceAdjustments ?? 0) + (subtotal.requirementsAdjustments ?? 0),
            totalCharge:
                totals.totalCharge + (subtotal.occurrenceTotalCharge ?? 0) + (subtotal.requirementsTotalCharge ?? 0),
        }),
        { listPrice: 0, adjustments: 0, totalCharge: 0 },
    );
}

// The edges of an expanded group carry the inset shadows that bracket it off from the rows around it.
function childRowClassName({ index, count }: { index: number; count: number }) {
    return [
        "expanded-child-row occurrence-child-row",
        index === 0 && "occurrence-child-row--first",
        index === count - 1 && "occurrence-child-row--last",
    ]
        .filter(Boolean)
        .join(" ");
}

// Shared by the row itself and by whatever wants to hide it, so the two can't drift apart.
function occurrenceSliceRowId(orgId: number, rsrvId: number, parent: LineItem) {
    return `occurrence-item-${orgId}-${rsrvId}-${parent.itemId}-${parent.itemType}`;
}

// An occurrence-billed item's price already reflects its occurrence adjustments, so the gap
// between list price and price is only a line item adjustment when the item carries one.
function lineItemAdjustment(item: LineItem) {
    if (!item.adjustmentAmt && !item.adjustmentPercent) return 0;
    return (item.price ?? 0) - (item.listPrice ?? 0);
}

@TypeManagerDecorator("s25-ng-invoice-view")
@Component({
    selector: "s25-ng-invoice-view",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25InvoiceControlPanelComponent,
        S25TableModule,
        S25TriggerRerenderModule,
        S25ModalModule,
        S25ButtonComponent,
        S25FadingSuccessMsgComponent,
        S25MoveItemAdjustmentsComponent,
        S25InvoiceTotalsComponent,
        S25CollapseChevronComponent,
        S25InvoiceNumberComponent,
        S25OrgPaymentsComponent,
        S25PricingFreshbooksComponent,
        S25PaymentRequestComponent,
    ],
    template: `
        @if (!readOnly()) {
            <s25-ng-invoice-control-panel />

            @if (isDefaultSetInvoice()) {
                <div class="cn-alert cn-alert--info c-margin-bottom--single">
                    This invoice is system generated and retains items not billed to other invoices. It cannot be
                    deleted, but it can be updated.
                </div>
            }
        }

        <!-- Billed items take just the view toggle; the rest of the control panel describes an
             invoice it is not showing. -->
        @if (readOnly() && viewedHasOccurrences()) {
            <div class="billed-items-toolbar">
                <span class="bi-label" aria-hidden="true">View</span>
                <div role="group" aria-label="Table view">
                    <button
                        type="button"
                        [class]="isOccurrenceView() ? 'c-textButton' : 'aw-button aw-button--primary'"
                        [attr.aria-pressed]="!isOccurrenceView()"
                        (click)="billedItemsView.set('lineItem')"
                    >
                        Line Item
                    </button>
                    <button
                        type="button"
                        [class]="isOccurrenceView() ? 'aw-button aw-button--primary' : 'c-textButton'"
                        [attr.aria-pressed]="isOccurrenceView()"
                        (click)="billedItemsView.set('occurrence')"
                    >
                        Occurrence
                    </button>
                </div>
            </div>
        }

        @if (moveMessage()) {
            <s25-ng-fading-success-msg [(message)]="moveMessage" />
        }

        @for (org of tableData(); track org.orgId) {
            @let expanded = !collapsedOrgIds().has(org.orgId);
            @let figures = orgFiguresById().get(org.orgId);
            <div>
                <div class="pricing-org-header">
                    <button
                        type="button"
                        class="org-toggle"
                        [attr.aria-expanded]="expanded"
                        [attr.aria-controls]="'pricing-org-table-' + org.orgId"
                        (click)="toggleOrg(org.orgId)"
                    >
                        <s25-ng-collapse-chevron
                            aria-hidden="true"
                            [collapsedOrientation]="'right'"
                            [expanded]="expanded"
                        />
                        <span class="org-name">{{ org.orgName }}</span>
                    </button>
                    @if (hiddenRowCount(org.orgId); as hiddenCount) {
                        <button type="button" class="c-textButton show-hidden-rows" (click)="showHiddenRows(org.orgId)">
                            Show {{ hiddenCount }} hidden {{ pluralize("row", hiddenCount) }}
                        </button>
                    }
                    @if (!readOnly() && invoiceNumberOrgIds().has(org.orgId)) {
                        <s25-ng-invoice-number class="org-figure" [orgId]="org.orgId" [orgName]="org.orgName" />
                        <span class="org-figure--rule" aria-hidden="true"></span>
                    }
                    <span class="org-figure">
                        <span class="org-figure--label">Total charge</span>
                        <span class="org-figure--value">{{ figures?.totalCharge }}</span>
                    </span>
                    <span class="org-figure--rule" aria-hidden="true"></span>
                    <span class="org-figure">
                        <span class="org-figure--label">Total payments</span>
                        <span class="org-figure--value">{{ figures?.payments }}</span>
                    </span>
                    <span class="org-figure--rule" aria-hidden="true"></span>
                    <span class="org-figure">
                        <span class="org-figure--label">Outstanding balance</span>
                        <span class="org-figure--value org-figure--value-strong">{{ figures?.balance }}</span>
                    </span>
                </div>
                <div [id]="'pricing-org-table-' + org.orgId">
                    @if (expanded) {
                        <s25-ng-table
                            *s25-ng-trigger-rerender="structureKey()"
                            [dataSource]="org.tableConfig"
                            [hasExpandableRows]="isOccurrenceView()"
                            [unlimitedWidth]="true"
                        />
                    }
                    @if (!readOnly() && paymentsStore.showPayments()) {
                        <s25-ng-org-payments [orgId]="org.orgId" [orgName]="org.orgName ?? ''" />
                    }
                    @if (!readOnly() && paymentsStore.freshbooksOrgIds().has(org.orgId)) {
                        <s25-ng-pricing-freshbooks [orgId]="org.orgId" />
                    }
                </div>
            </div>
        }

        <s25-ng-invoice-totals [invoice]="viewedInvoice()" />

        @if (!readOnly()) {
            <s25-ng-payment-request />
        }

        <s25-ng-modal #moveModal [type]="'custom'" [title]="moveModalTitle()" [size]="'md'">
            <ng-template #s25ModalBody>
                @if (pendingMove(); as pending) {
                    @if (pending.kind === "remove") {
                        <p>
                            Remove <b>{{ pending.label }}</b> from <b>{{ displayName(pending.source) }}</b
                            >? Its line items will no longer be billed on this invoice.
                        </p>
                    }
                    @if (pending.kind === "move") {
                        <s25-ng-move-item-adjustments
                            [item]="pending.item"
                            [itemLabel]="pending.label"
                            [source]="pending.source"
                            [target]="pending.target"
                            [adjustedItems]="pending.adjustedItems"
                        />
                    }
                }
            </ng-template>
            <ng-template #s25ModalFooter>
                <div class="move-modal-buttons">
                    <s25-ng-button
                        [type]="pendingMove()?.kind === 'remove' ? 'danger' : 'primary'"
                        [onClick]="confirmMove"
                    >
                        {{ pendingMove()?.kind === "remove" ? "Remove" : "Save" }}
                    </s25-ng-button>
                    <s25-ng-button [type]="'outline'" (clicked)="cancelMove()">Cancel</s25-ng-button>
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .billed-items-toolbar {
            display: flex;
            align-items: center;
            gap: 0.5rem;

            .bi-label {
                font-weight: 600;
            }

            div {
                display: flex;
                gap: 0.25rem;
            }
        }

        .pricing-org-header {
            display: flex;
            align-items: flex-start;
            gap: 1.25rem;
            padding: 0.875rem 1.25rem;
            background: var(--color--table-header);
            border-bottom: 1px solid var(--color--table-border);
        }

        .org-toggle {
            display: flex;
            align-items: center;
            align-self: center;
            gap: 1.25rem;
            flex: 1;
            min-width: 0;
            padding: 0;
            background: none;
            border: none;
            color: var(--color--text);
            font: inherit;
            text-align: left;
            cursor: pointer;
            user-select: none;
        }

        .org-toggle:focus-visible {
            outline-offset: 4px;
        }

        .org-name {
            font-size: 18px;
            font-weight: 600;
        }

        .org-figure {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
            text-align: right;
        }

        .org-figure--label {
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .org-figure--value {
            font-size: 17px;
            font-weight: 600;
        }

        .org-figure--value-strong {
            color: var(--color--text-strong);
        }

        .org-figure--rule {
            width: 1px;
            align-self: stretch;
            background: var(--color--border);
        }

        .show-hidden-rows {
            align-self: center;
            white-space: nowrap;
        }

        :host ::ng-deep .tax-name {
            font-size: 90%;
        }

        :host ::ng-deep table {
            table-layout: fixed;
        }

        :host ::ng-deep s25-ng-table .rose-object-table-header {
            margin-bottom: 0;
        }

        :host ::ng-deep .subtotal-row {
            border-top: 2px solid #555;
        }

        :host ::ng-deep s25-ng-table table.s25-ng-table--table > tbody > tr.subtotal-row > td.s25-ng-table--cell {
            padding-top: 1em !important;
        }

        :host ::ng-deep .occurrence-child-row > td.s25-ng-table--cell[col="item"] {
            padding-left: 2em;
        }

        :host ::ng-deep .occurrence-child-row--first > td.s25-ng-table--cell {
            box-shadow: inset 0 11px 8px -10px var(--color--table-border);
        }

        :host ::ng-deep .occurrence-child-row--last > td.s25-ng-table--cell {
            box-shadow: inset 0 -11px 8px -10px var(--color--table-border);
        }

        :host ::ng-deep .occurrence-child-row--first.occurrence-child-row--last > td.s25-ng-table--cell {
            box-shadow:
                inset 0 11px 8px -10px var(--color--table-border),
                inset 0 -11px 8px -10px var(--color--table-border);
        }

        .move-modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
        }
    `,
})
export class S25InvoiceViewComponent {
    private readonly pricingStore = inject(PricingStore);
    protected readonly paymentsStore = inject(PaymentsStore);

    protected readonly pluralize = S25Util.pluralize;

    /** Which invoice to render. Defaults to the one the sidebar has selected. */
    readonly invoice = input<Invoice | undefined>(undefined);
    readonly mode = input<InvoiceViewMode>("invoice");

    /**
     * Billed items render the reference set, which only records what the other invoices bill. Its
     * amounts cannot be written, so the one control left is Invoice To - and that moves each row
     * between the invoices that actually bill it.
     */
    protected readonly readOnly = computed(() => this.mode() === "billedItems");

    protected readonly displayName = invoiceDisplayName;

    readonly viewedInvoice = computed(() => this.invoice() ?? this.pricingStore.selectedInvoice());
    protected readonly isDefaultSetInvoice = computed(() => isDefaultSet(this.viewedInvoice()));
    readonly combineRelated = this.pricingStore.combineRelated;
    readonly organizations = this.pricingStore.organizations;
    readonly eventId = this.pricingStore.eventId;

    /** Billed items carry their own view, since the store's tracks the selected invoice. */
    readonly billedItemsView = signal<PricingView>("occurrence");
    protected readonly viewedHasOccurrences = computed(() =>
        this.viewedInvoice()?.billing?.lineItems?.some(
            (item) => !!item.occurrences?.length && (this.combineRelated() || item.eventId === this.eventId()),
        ),
    );
    readonly isOccurrenceView = computed(() =>
        this.readOnly()
            ? this.billedItemsView() === "occurrence" && !!this.viewedHasOccurrences()
            : this.pricingStore.isOccurrenceView(),
    );
    readonly canEdit = computed(
        () => !this.readOnly() && this.pricingStore.canEditPricing() && !this.viewedInvoice()?.billDefn?.completed,
    );

    // A date range invoice decides its own occurrences, so nothing can be moved on or off it.
    readonly canMoveItems = computed(() => {
        if (!this.isOccurrenceView() || !this.pricingStore.canEditPricing()) return false;
        // Billed items move rows off whichever invoice bills them, so the invoice on screen says
        // nothing about whether a move is possible.
        if (this.readOnly()) return true;
        return this.canEdit() && !InvoiceMove.isDateRangeInvoice(this.viewedInvoice());
    });

    private owningInvoiceId(item: Pick<LineItem, "billId">): number | undefined {
        return item.billId ?? this.viewedInvoice()?.id;
    }

    /**
     * Whether a row can be edited. A merged default set is only as locked as the set a given row
     * sits on, so this follows the row's own invoice rather than the one the tab names.
     */
    protected canEditRow(item: Pick<LineItem, "billId">): boolean {
        if (!this.canEdit()) return false;

        const billId = item.billId;
        if (!billId || billId === this.viewedInvoice()?.id) return true;
        return !this.pricingStore.invoiceById().get(billId)?.billDefn?.completed;
    }

    /**
     * The invoice each occurrence and requirement is billed on, which is what a billed items row
     * moves off of. A user-created invoice wins over the default set, since that is where an item
     * lands once one claims it.
     */
    private readonly billingInvoiceByItem = computed(() => {
        const byRsrvId = new Map<number, Invoice>(),
            byRequirementId = new Map<number, Invoice>();
        if (!this.readOnly()) return { byRsrvId, byRequirementId };

        const invoices = [...this.pricingStore.listedInvoices()].sort(
            (first, second) => Number(isDefaultSet(first)) - Number(isDefaultSet(second)),
        );
        for (const invoice of invoices) {
            const ids = InvoiceMove.invoiceObjectIds(invoice);
            for (const rsrvId of ids.rsrvIds) if (!byRsrvId.has(rsrvId)) byRsrvId.set(rsrvId, invoice);
            for (const reqId of ids.reqIds) if (!byRequirementId.has(reqId)) byRequirementId.set(reqId, invoice);
        }
        return { byRsrvId, byRequirementId };
    });

    private readonly orgTables = viewChildren(S25TableComponent);

    readonly moveStatus = signal<Record<string, { loading?: boolean; error?: string }>>({});
    moveMessage = signal("");
    readonly pendingMove = signal<PendingMove | undefined>(undefined);
    readonly moveModalTitle = computed(() =>
        this.pendingMove()?.kind === "remove" ? "Remove From Invoice" : "Line Item Adjustments",
    );

    private readonly moveModal = viewChild("moveModal", { read: S25ModalComponent });
    private readonly allocationForm = viewChild(S25MoveItemAdjustmentsComponent);

    statusObj = linkedSignal<Record<string, { loading: boolean; error: string }>>(() =>
        Object.fromEntries(
            (this.viewedInvoice()?.billing?.lineItems ?? []).map((item) => [
                this.getLineItemUUID(item),
                { loading: false, error: "" },
            ]),
        ),
    );

    // Only changes that the table reads at init time remount it; data changes go through refresh()
    // so an expanded occurrence survives an adjustment save.
    readonly structureKey = computed(() =>
        JSON.stringify({
            canEdit: this.canEdit(),
            columns: this.columns().map((column) => column.id),
            combined: this.combineRelated(),
            occurrenceView: this.isOccurrenceView(),
            readOnly: this.readOnly(),
        }),
    );

    readonly columns = computed<Table.Column[]>(() => {
        const hiddenColumns = this.pricingStore.hiddenColumns();
        return PricingColumns.pricingColumns(this.combineRelated(), this.canMoveItems()).filter(
            (column) => !hiddenColumns.has(String(column.id)),
        );
    });

    readonly invoiceToOptions = computed(() => [
        { value: "", label: "Select Invoice", disabled: true },
        { value: "new", label: "Create New" },
        { value: "remove", label: "Remove" },
        ...this.pricingStore
            .displayedInvoices()
            .filter((invoice) => !InvoiceMove.isDateRangeInvoice(invoice))
            .map((invoice) => ({
                value: invoice.id,
                label: `${invoiceDisplayName(invoice)}${invoice.billDefn?.completed ? " (Locked)" : ""}`,
                disabled: !!invoice.billDefn?.completed,
            })),
    ]);

    /** Which option stands for an invoice: a folded default set reads as the Standard Invoice. */
    private optionValueFor(invoice: Invoice): number {
        if (!isDefaultSet(invoice)) return invoice.id;
        return this.pricingStore.displayedInvoices().find(isDefaultSet)?.id ?? invoice.id;
    }

    private optionsForSource(source: Invoice) {
        const options = isDefaultSet(source)
            ? this.invoiceToOptions().filter((option) => option.value !== "remove")
            : this.invoiceToOptions();

        const selected = this.optionValueFor(source);
        if (options.some((option) => option.value === selected)) return options;
        return [...options, { value: selected, label: invoiceDisplayName(source), disabled: true }];
    }

    /** The default set holding an event's unclaimed billing. */
    private defaultSetForEvent(eventId: number | undefined): Invoice | undefined {
        if (S25Util.isUndefined(eventId)) return undefined;

        const defaultSetId = this.pricingStore.defaultSetIdByEvent().get(eventId);
        return defaultSetId ? this.pricingStore.invoiceById().get(defaultSetId) : undefined;
    }

    private moveSource(item: InvoiceMove.MovableItem, eventId: number | undefined): Invoice | undefined {
        const eventDefaultSet = this.defaultSetForEvent(eventId);

        if (!this.readOnly()) {
            const viewed = this.viewedInvoice();
            return isDefaultSet(viewed) ? (eventDefaultSet ?? viewed) : viewed;
        }

        const { byRsrvId, byRequirementId } = this.billingInvoiceByItem();
        const billedOn = item.rsrvId ? byRsrvId.get(item.rsrvId) : byRequirementId.get(item.requirementId!);
        return billedOn ?? eventDefaultSet;
    }

    readonly orgSelectOptions = computed(() =>
        this.organizations().map(({ itemId, itemName }) => ({ value: Number(itemId), label: itemName ?? "" })),
    );

    readonly billingItemsByOrg = computed(() => {
        const profileNames = this.pricingStore.profileNameById();

        return (
            this.viewedInvoice()?.billing?.lineItems?.reduce((map: Record<number, LineItem[]>, lineItem) => {
                const orgId = lineItem.chargeToId,
                    billedEventId = lineItem.eventId;
                if (!orgId || (!this.combineRelated() && billedEventId !== this.eventId())) return map;

                const profileName = lineItem.profileId ? profileNames.get(lineItem.profileId) : undefined;
                const named: LineItem = { ...lineItem, itemName: lineItemLabel(lineItem.itemName, profileName) };

                if (map[orgId]) map[orgId].push(named);
                else map[orgId] = [named];
                return map;
            }, {}) ?? {}
        );
    });

    // Requirements are the only line item type without occurrences, so the split is on the
    // presence of occurrences rather than on itemType - event types group like anything else.
    readonly occurrenceItemsByOrg = computed(() => {
        const map = new Map<number, LineItem[]>();
        for (const [orgId, lineItems] of Object.entries(this.billingItemsByOrg())) {
            map.set(
                Number(orgId),
                lineItems.filter((lineItem) => !!lineItem.occurrences?.length),
            );
        }
        return map;
    });

    readonly standaloneItemsByOrg = computed(() => {
        const map = new Map<number, LineItem[]>();
        for (const [orgId, lineItems] of Object.entries(this.billingItemsByOrg())) {
            map.set(
                Number(orgId),
                lineItems.filter((lineItem) => !lineItem.occurrences?.length),
            );
        }
        return map;
    });

    readonly occurrenceGroupsByOrg = computed(() => {
        const occurrenceMap = this.pricingStore.occurrences(),
            dateFormats = this.pricingStore.dateFormats(),
            map = new Map<number, OccurrenceGroup[]>();

        for (const [orgId, lineItems] of this.occurrenceItemsByOrg()) {
            const groups = new Map<number, OccurrenceGroup>();
            for (const parent of lineItems) {
                for (const slice of parent.occurrences ?? []) {
                    const group = groups.get(slice.rsrvId);
                    if (group) group.slices.push({ slice, parent });
                    else
                        groups.set(slice.rsrvId, {
                            rsrvId: slice.rsrvId,
                            label: "",
                            eventId: parent.eventId,
                            slices: [{ slice, parent }],
                        });
                }
            }
            if (!groups.size) continue;

            for (const group of groups.values()) {
                group.slices.sort((first, second) =>
                    (first.parent.itemName ?? "").localeCompare(second.parent.itemName ?? ""),
                );
            }

            const sorted = [...groups.values()].sort(
                (first, second) =>
                    +new Date(occurrenceMap[first.rsrvId]?.rsrvStartDt as string) -
                    +new Date(occurrenceMap[second.rsrvId]?.rsrvStartDt as string),
            );

            // Reuse the legacy formatter so labels match the old view, including the fallback to
            // times when two occurrences land on the same date.
            const labels: Partial<Occurrence>[] = sorted.map((group) => ({
                rsrvId: group.rsrvId,
                occurrence: occurrenceMap[group.rsrvId]?.occurrence ?? undefined,
            }));
            S25PricingUtil.formatOccDates(labels, {
                ...occurrenceMap,
                dateFormat: dateFormats?.date,
                timeFormat: dateFormats?.time,
            } as unknown as OccDataMap);
            labels.forEach((label, index) => (sorted[index].label = label.occurrence ?? ""));

            map.set(orgId, sorted);
        }
        return map;
    });

    readonly hideNoChargeItems = this.pricingStore.hideNoChargeItems;

    readonly zeroTotalRowIdsByOrg = computed<ReadonlyMap<number, ReadonlySet<string>>>(() => {
        const byOrg = new Map<number, Set<string>>();
        const addRow = (orgId: number, rowId: string) => {
            const ids = byOrg.get(orgId);
            if (ids) ids.add(rowId);
            else byOrg.set(orgId, new Set([rowId]));
        };

        if (!this.isOccurrenceView()) {
            for (const [orgId, lineItems] of Object.entries(this.billingItemsByOrg())) {
                for (const item of lineItems) if (!item.total) addRow(Number(orgId), this.getLineItemUUID(item));
            }
            return byOrg;
        }

        for (const [orgId, groups] of this.occurrenceGroupsByOrg()) {
            for (const group of groups) {
                for (const { slice, parent } of group.slices) {
                    if (!slice.total) addRow(orgId, occurrenceSliceRowId(orgId, group.rsrvId, parent));
                }
            }
        }
        for (const [orgId, lineItems] of this.standaloneItemsByOrg()) {
            for (const item of lineItems) if (!item.total) addRow(orgId, this.getLineItemUUID(item));
        }
        return byOrg;
    });

    // Hides survive a pricing change so an adjustment save doesn't undo them, but the rows on a
    // different invoice or in the other view are different rows, so they start over.
    private readonly hiddenRowsResetKey = computed(() => `${this.viewedInvoice()?.id}-${this.isOccurrenceView()}`);

    private readonly manuallyHiddenRowIds = linkedSignal<string, ReadonlySet<string>>({
        source: this.hiddenRowsResetKey,
        computation: () => new Set<string>(),
    });

    // A row that stops reading $0 - because it was adjusted, say - comes back on its own.
    readonly hiddenRowIdsByOrg = computed<ReadonlyMap<number, ReadonlySet<string>>>(() => {
        const hidden = this.manuallyHiddenRowIds();
        if (!hidden.size) return new Map();

        return new Map(
            [...this.zeroTotalRowIdsByOrg()].map(([orgId, zeroTotalIds]): [number, ReadonlySet<string>] => [
                orgId,
                new Set([...zeroTotalIds].filter((rowId) => hidden.has(rowId))),
            ]),
        );
    });

    // The checkbox and the per-row icon converge here, so the rows are built against one set.
    private readonly suppressedRowIdsByOrg = computed(() =>
        this.hideNoChargeItems() ? this.zeroTotalRowIdsByOrg() : this.hiddenRowIdsByOrg(),
    );

    private hideRow(rowId: string) {
        this.manuallyHiddenRowIds.update((hidden) => new Set(hidden).add(rowId));
    }

    // Nothing to put back while the checkbox is hiding every no-charge row anyway.
    protected hiddenRowCount(orgId: number): number {
        if (this.hideNoChargeItems()) return 0;
        return this.hiddenRowIdsByOrg().get(orgId)?.size ?? 0;
    }

    showHiddenRows(orgId: number) {
        const restored = this.hiddenRowIdsByOrg().get(orgId);
        if (!restored?.size) return;
        this.manuallyHiddenRowIds.update((hidden) => new Set([...hidden].filter((rowId) => !restored.has(rowId))));
    }

    readonly occurrenceSubtotalsByOrg = computed(() => {
        const map = new Map<number, Map<number, AccountOccurrenceSubtotal>>();
        for (const subtotal of this.viewedInvoice()?.billing?.subtotals ?? []) {
            for (const occurrence of subtotal.accountOccurrence ?? []) {
                const orgId = occurrence.chargeToId;
                if (!orgId) continue;
                const byRsrvId = map.get(orgId) ?? new Map<number, AccountOccurrenceSubtotal>();
                byRsrvId.set(occurrence.rsrvId, occurrence);
                map.set(orgId, byRsrvId);
            }
        }
        return map;
    });

    readonly subtotalsByOrg = computed(() =>
        InvoiceTotals.subtotalsByOrg(this.viewedInvoice(), {
            combineRelated: this.combineRelated(),
            eventId: this.eventId(),
        }),
    );

    readonly invoiceSubtotalByOrg = computed(() => InvoiceTotals.invoiceSubtotalByOrg(this.viewedInvoice()));

    readonly invoiceNumberOrgIds = computed(() => new Set(this.invoiceSubtotalByOrg().keys()));

    readonly adjustmentsByOrg = computed(() => InvoiceTotals.adjustmentsByOrg(this.viewedInvoice()));

    // An org can be paid without being charged anything on this invoice. It still needs a table so
    // it stays visible and can take a gross adjustment. Unlike the payment figures, which only sum
    // what has actually been collected, a voided or pending payment is enough to keep the org in view.
    readonly paymentOrgIds = computed(() => {
        const scope = { combineRelated: this.combineRelated(), eventId: this.eventId() };
        const orgIds = new Set<number>();
        for (const payment of this.viewedInvoice()?.payments ?? []) {
            if (payment.organizationId && InvoiceTotals.isPaymentInScope(payment, scope))
                orgIds.add(payment.organizationId);
        }
        return orgIds;
    });

    readonly orgNameById = this.pricingStore.orgNameById;

    // Every org the invoice touches: billed, adjusted, or paid.
    readonly orgIds = computed(
        () =>
            new Set([
                ...Object.keys(this.billingItemsByOrg()).map(Number),
                ...this.adjustmentsByOrg().keys(),
                ...this.paymentOrgIds(),
            ]),
    );

    // One sum per org per pricing change, shared by the header figures and each table's footer.
    readonly orgTotalsById = computed(() => {
        const subtotals = this.subtotalsByOrg();
        return new Map(
            [...this.orgIds()].map((orgId) => [orgId, InvoiceTotals.sumSubtotals(subtotals.get(orgId) ?? [])]),
        );
    });

    readonly orgFiguresById = computed(
        () =>
            new Map(
                [
                    ...InvoiceTotals.orgFiguresByOrg(this.viewedInvoice(), this.orgIds(), {
                        combineRelated: this.combineRelated(),
                        eventId: this.eventId(),
                    }),
                ].map(([orgId, figures]): [number, OrgFigures] => [
                    orgId,
                    {
                        totalCharge: currency(figures.charge),
                        payments: currency(figures.paid),
                        balance: currency(figures.balance),
                    },
                ]),
            ),
    );

    readonly tableData = computed<OrgTable[]>(() => {
        const columns = this.columns(),
            orgNameById = this.orgNameById();

        return [...this.orgIds()]
            .map((orgId) => ({
                orgName: orgNameById.get(orgId) ?? S25Const.private,
                orgId,
                tableConfig: {
                    type: "unpaginated" as const,
                    dataSource: () => Promise.resolve({ rows: this.buildRows(orgId) }),
                    columns,
                },
            }))
            .sort((first, second) => (first.orgName ?? "").localeCompare(second.orgName ?? ""));
    });

    readonly collapsedOrgIds = linkedSignal<Set<number>, ReadonlySet<number>>({
        source: () => this.orgIds(),
        computation: (billedOrgIds, previous) =>
            new Set([...(previous?.value ?? [])].filter((orgId) => billedOrgIds.has(orgId))),
    });

    toggleOrg(orgId: number) {
        this.collapsedOrgIds.update((collapsed) => {
            const next = new Set(collapsed);
            if (!next.delete(orgId)) next.add(orgId);
            return next;
        });
    }

    constructor() {
        effect(() => {
            // Re-read rows in place on any pricing change so expansion and scroll position survive.
            this.viewedInvoice();
            this.isOccurrenceView();
            this.suppressedRowIdsByOrg();
            untracked(() => {
                for (const table of this.orgTables()) void table.refresh(false, false, true);
            });
        });
    }

    private buildRows(orgId: number): Table.Row[] {
        const rows = this.isOccurrenceView() ? this.occurrenceRows(orgId) : this.lineItemRows(orgId);
        // An org billed nothing still needs one row so the table renders its footer, which may
        // carry gross adjustments.
        return [...(rows.length ? rows : [this.noItemsRow(orgId)]), ...this.footerRows(orgId)];
    }

    private noItemsRow(orgId: number): Table.Row {
        const zero = S25PricingUtil.formatCurrency(0);
        const cells: Table.Row["cells"] = {
            item: { text: "(none)" },
            listPrice: { text: zero },
            adjust: { text: zero },
            price: { text: zero },
            tax: { text: zero },
            total: { text: zero },
        };

        if (this.combineRelated()) cells.reference = { text: "" };

        return { id: `no-items-${orgId}`, name: "(none)", cells };
    }

    private suppressedRowIds(orgId: number): ReadonlySet<string> {
        return this.suppressedRowIdsByOrg().get(orgId) ?? new Set();
    }

    private lineItemRows(orgId: number): Table.Row[] {
        const suppressed = this.suppressedRowIds(orgId);
        return this.sortByName(this.billingItemsByOrg()[orgId] ?? [])
            .filter((item) => !suppressed.has(this.getLineItemUUID(item)))
            .map(this.mapToRows);
    }

    private occurrenceRows(orgId: number): Table.Row[] {
        const groups = this.occurrenceGroupsByOrg().get(orgId) ?? [],
            subtotals = this.occurrenceSubtotalsByOrg().get(orgId),
            suppressed = this.suppressedRowIds(orgId);

        return [
            ...groups.map((group) => this.occurrenceRow(orgId, group, subtotals?.get(group.rsrvId))),
            ...this.sortByName(this.standaloneItemsByOrg().get(orgId) ?? [])
                .filter((item) => !suppressed.has(this.getLineItemUUID(item)))
                .map(this.mapToRows),
        ];
    }

    private occurrenceRow(
        orgId: number,
        group: OccurrenceGroup,
        subtotal: AccountOccurrenceSubtotal | undefined,
    ): Table.Row {
        const totalCharge = subtotal?.occurrenceTotalCharge ?? 0;
        const cells: Table.Row["cells"] = {
            item: { text: group.label },
            listPrice: { text: S25PricingUtil.formatCurrency(subtotal?.occurrenceListPrice ?? 0) },
            adjust: { text: S25PricingUtil.formatCurrency(subtotal?.occurrenceAdjustments ?? 0) },
            price: { text: S25PricingUtil.formatCurrency(totalCharge) },
            total: { text: S25PricingUtil.formatCurrency(totalCharge) },
        };

        if (this.combineRelated()) {
            cells.reference = { text: this.pricingStore.eventLocatorMap()[group.eventId!] ?? "" };
        }

        const rowId = `occurrence-${orgId}-${group.rsrvId}`;
        if (this.canMoveItems()) {
            cells.invoiceTo = this.invoiceToCell(rowId, { rsrvId: group.rsrvId }, group.label, group.eventId);
        }

        // The occurrence still charges what it charges, so it keeps its row even when every line
        // item under it has been hidden - there is just nothing left to expand.
        const suppressed = this.suppressedRowIds(orgId);
        const slices = group.slices.filter(
            ({ parent }) => !suppressed.has(occurrenceSliceRowId(orgId, group.rsrvId, parent)),
        );

        return {
            id: rowId,
            name: group.label,
            expandable: !!slices.length,
            expandedContent: {
                expandAsRows: true,
                rows: slices.map(({ slice, parent }, index) =>
                    this.occurrenceSliceRow(orgId, group, slice, parent, {
                        index,
                        count: slices.length,
                    }),
                ),
            },
            cells,
        };
    }

    private occurrenceSliceRow(
        orgId: number,
        group: OccurrenceGroup,
        slice: PricingOccurrence,
        parent: LineItem,
        position: { index: number; count: number },
    ): Table.Row {
        const occurrence: OccurrenceAdjustmentTarget = {
            rsrvId: group.rsrvId,
            itemId: parent.itemId!,
            itemType: parent.itemType!,
            billId: parent.billId,
            profileId: parent.profileId,
            itemName: parent.itemName,
            occurrenceLabel: group.label,
            chargeToId: parent.chargeToId,
            listPrice: slice.listPrice,
            adjustmentAmt: slice.adjustmentAmt,
            adjustmentPercent: slice.adjustmentPercent,
            adjustmentName: slice.adjustmentName,
        };

        const rowId = occurrenceSliceRowId(orgId, group.rsrvId, parent);

        const cells: Table.Row["cells"] = {
            item: this.itemNameCell(parent.itemName, rowId, !slice.total),
            listPrice: { text: S25PricingUtil.formatCurrency(slice.listPrice ?? 0) },
            adjust: {
                component: S25PricingAdjustmentCellComponent,
                inputs: { occurrence, readOnly: this.readOnly() },
            },
            price: { text: S25PricingUtil.formatCurrency(slice.price ?? 0) },
            total: { text: S25PricingUtil.formatCurrency(slice.total ?? 0) },
            charge: this.chargeCell(parent, this.getLineItemUUID(parent)),
            schedule: { text: this.pricingStore.rateScheduleNameMap()[parent.rateId!] ?? "" },
            group: this.rateGroupCell(parent),
            debit: { text: parent.debitAccountNumber },
            credit: { text: parent.creditAccountNumber },
        };

        if (this.combineRelated()) {
            cells.reference = { text: this.pricingStore.eventLocatorMap()[parent.eventId!] ?? "" };
        }

        return {
            id: rowId,
            name: parent.itemName ?? "",
            className: childRowClassName(position),
            cells,
        };
    }

    private sortByName(lineItems: LineItem[]) {
        return [...lineItems].sort((first, second) => (first.itemName ?? "").localeCompare(second.itemName ?? ""));
    }

    /** A line item charging nothing offers the button that takes its row out of the table. */
    private itemNameCell(itemName: string | undefined, rowId: string, hideable: boolean): Table.Cell {
        if (!hideable) return { text: itemName };

        return {
            component: S25PricingHideableItemComponent,
            inputs: { itemName: itemName ?? "" },
            outputs: { hide: () => this.hideRow(rowId) },
            // Component cells are opaque to the table's own filtering and sorting without this.
            textValue: itemName ?? "",
        } satisfies Table.Cell<S25PricingHideableItemComponent>;
    }

    @Bind
    mapToRows(item: LineItem): Table.Row {
        const uuid = this.getLineItemUUID(item);
        const cells: Table.Row["cells"] = {
            item: this.itemNameCell(item.itemName, uuid, !item.total),
            listPrice: { text: S25PricingUtil.formatCurrency(item.listPrice || 0) },
            adjust: {
                component: S25PricingAdjustmentCellComponent,
                inputs: { lineItem: item, readOnly: this.readOnly() },
            },
            price: { text: S25PricingUtil.formatCurrency(item.price || 0) },
            tax: {
                component: GenericTableRenderHTMLComponent,
                inputs: {
                    text:
                        item.taxes
                            ?.map(
                                ({ taxName, taxCharge }, index) =>
                                    `<p class="tax-name">${taxName}:</p>
                                     <p${index < item.taxes!.length - 1 ? ' class="c-margin-bottom--quarter"' : ""}>${S25PricingUtil.formatCurrency(taxCharge || 0)}</p>`,
                            )
                            ?.join("") ?? "",
                    isHtml: S25Util.isDefined(item.taxes) && !!item.taxes.length,
                },
            },
            total: { text: S25PricingUtil.formatCurrency(item.total ?? 0) },
            charge: this.chargeCell(item, uuid),
            schedule: { text: this.pricingStore.rateScheduleNameMap()[item.rateId!] ?? "" },
            group: this.rateGroupCell(item),
            debit: { text: item.debitAccountNumber },
            credit: { text: item.creditAccountNumber },
        };

        if (this.combineRelated()) {
            cells.reference = { text: this.pricingStore.eventLocatorMap()[item.eventId!] ?? "" };
        }

        // Requirements are the only line items that stand outside an occurrence, so they are the
        // only ones this view moves on their own.
        if (this.canMoveItems() && item.itemType === lineItemType.REQUIREMENT) {
            cells.invoiceTo = this.invoiceToCell(
                uuid,
                { requirementId: item.itemId! },
                item.itemName ?? "",
                item.eventId,
            );
        }

        return { id: uuid, name: item.itemName ?? uuid, cells };
    }

    private invoiceToCell(
        rowKey: string,
        item: InvoiceMove.MovableItem,
        label: string,
        eventId: number | undefined,
    ): Table.Cell {
        const source = this.moveSource(item, eventId);
        // A locked invoice keeps its rows, so its billed items only report where they are billed.
        if (!source || source.billDefn?.completed) return { text: invoiceDisplayName(source) };

        return {
            component: GenericTableSimpleSelectComponent,
            inputs: {
                modelValue: this.optionValueFor(source),
                options: this.optionsForSource(source),
                resetAfterSelection: true,
                isLoading: computed(() => this.moveStatus()[rowKey]?.loading ?? false),
                errorMsg: computed(() => this.moveStatus()[rowKey]?.error ?? ""),
            },
            outputs: {
                modelValueChange: (selected) => this.onInvoiceToChange(selected, item, rowKey, label, eventId),
            },
        } satisfies Table.Cell<GenericTableSimpleSelectComponent>;
    }

    // Charge To and Rate Group belong to the whole line item, so an occurrence slice edits the
    // same record - and shares its loading and error state - as the line item view row.
    private chargeCell(item: LineItem, uuid: string): Table.Cell {
        if (!this.canEditRow(item)) return { text: this.orgNameById().get(item.chargeToId!) ?? "" };

        return {
            component: GenericTableSimpleSelectComponent,
            inputs: {
                modelValue: item.chargeToId,
                options: this.orgSelectOptions(),
                isLoading: computed(() => this.statusObj()[uuid]?.loading ?? false),
                errorMsg: computed(() => this.statusObj()[uuid]?.error ?? ""),
            },
            outputs: {
                modelValueChange: (selected) => this.onOrgChange(item, selected, uuid),
            },
        } satisfies Table.Cell<GenericTableSimpleSelectComponent>;
    }

    private rateGroupCell(item: LineItem) {
        return {
            component: S25PricingRateGroupDropdown,
            inputs: { lineItem: item, readOnly: this.readOnly() },
        } satisfies Table.Cell<S25PricingRateGroupDropdown>;
    }

    footerRows(orgId: number): Table.Row[] {
        const subtotals = this.subtotalsByOrg().get(orgId) ?? [],
            totals = this.orgTotalsById().get(orgId) ?? InvoiceTotals.sumSubtotals([]),
            occurrenceTotals = sumOccurrenceSubtotals(subtotals),
            invoiceSubtotal = this.invoiceSubtotalByOrg().get(orgId),
            combineRelated = this.combineRelated();

        const grossAdjustments = InvoiceTotals.orgGrossAdjustments(totals, invoiceSubtotal, combineRelated);
        const grandTotal = InvoiceTotals.orgGrandTotal(totals, invoiceSubtotal, combineRelated);

        // Occurrence view lists pre-adjustment occurrence list prices, so its total row adds the
        // occurrence adjustments back; line item view's list prices already include them. Either
        // way the price column lands on `taxableAmount + grossAdjustments`.
        const netAdjustments = this.isOccurrenceView()
            ? occurrenceTotals.adjustments + totals.profileAdjustments + grossAdjustments
            : totals.requirementsAdjustments + totals.profileAdjustments + grossAdjustments;

        const leadingRows = this.isOccurrenceView()
            ? this.occurrenceSubtotalRows(orgId, occurrenceTotals)
            : [this.lineItemSubtotalRow(orgId, totals)];

        return [
            ...leadingRows,
            ...this.adjustmentRows(orgId),
            this.totalRow(orgId, {
                listPrice: this.isOccurrenceView() ? occurrenceTotals.listPrice : totals.listPrice,
                netAdjustments,
                tax: totals.tax,
                grandTotal,
            }),
        ];
    }

    private lineItemSubtotalRow(orgId: number, totals: ReturnType<typeof InvoiceTotals.sumSubtotals>): Table.Row {
        return {
            id: `subtotal-${orgId}`,
            name: "Subtotal",
            className: "subtotal-row",
            cells: {
                item: { text: "Subtotal" },
                listPrice: { text: S25PricingUtil.formatCurrency(totals.listPrice) },
                adjust: {
                    text: S25PricingUtil.formatCurrency(totals.profileAdjustments + totals.requirementsAdjustments),
                },
                price: { text: S25PricingUtil.formatCurrency(totals.taxableAmount) },
                tax: { text: S25PricingUtil.formatCurrency(totals.tax) },
                total: { text: S25PricingUtil.formatCurrency(totals.eventsTotalCharge) },
            },
        };
    }

    /**
     * Occurrence view splits the subtotal in two: occurrence-level amounts, then the line item
     * adjustments and taxes that apply on top of them.
     */
    private occurrenceSubtotalRows(
        orgId: number,
        occurrenceTotals: ReturnType<typeof sumOccurrenceSubtotals>,
    ): Table.Row[] {
        const occurrenceItems = this.occurrenceItemsByOrg().get(orgId) ?? [],
            standaloneItems = this.standaloneItemsByOrg().get(orgId) ?? [];

        const lineItemAdjustments = occurrenceItems.reduce((sum, item) => sum + lineItemAdjustment(item), 0),
            lineItemTaxes = occurrenceItems.reduce((sum, item) => sum + (item.tax ?? 0), 0),
            standaloneTaxes = standaloneItems.reduce((sum, item) => sum + (item.tax ?? 0), 0);

        const subtotalRow: Table.Row = {
            id: `subtotal-${orgId}`,
            name: "Subtotal",
            className: "subtotal-row",
            cells: {
                item: { text: "Subtotal" },
                listPrice: { text: S25PricingUtil.formatCurrency(occurrenceTotals.listPrice) },
                adjust: { text: S25PricingUtil.formatCurrency(occurrenceTotals.adjustments) },
                price: { text: S25PricingUtil.formatCurrency(occurrenceTotals.totalCharge) },
                tax: { text: S25PricingUtil.formatCurrency(standaloneTaxes) },
                total: { text: S25PricingUtil.formatCurrency(occurrenceTotals.totalCharge) },
            },
        };

        const expandedItems = occurrenceItems.filter((item) => lineItemAdjustment(item) !== 0 || (item.tax ?? 0) !== 0);

        const lineItemRow: Table.Row = {
            id: `line-item-amounts-${orgId}`,
            name: "Occurrence Line Item Amounts",
            expandable: !!expandedItems.length,
            expandedContent: {
                expandAsRows: true,
                rows: this.sortByName(expandedItems).map((item, index) =>
                    this.lineItemAmountRow(orgId, item, { index, count: expandedItems.length }),
                ),
            },
            cells: {
                item: { text: "Occurrence Line Item Amounts" },
                adjust: { text: S25PricingUtil.formatCurrency(lineItemAdjustments) },
                tax: { text: S25PricingUtil.formatCurrency(lineItemTaxes) },
                total: { text: S25PricingUtil.formatCurrency(lineItemAdjustments + lineItemTaxes) },
            },
        };

        return [subtotalRow, lineItemRow];
    }

    private lineItemAmountRow(orgId: number, item: LineItem, position: { index: number; count: number }): Table.Row {
        const adjustment = lineItemAdjustment(item),
            tax = item.tax ?? 0;

        const cells: Table.Row["cells"] = {
            item: { text: item.itemName },
            adjust: { text: S25PricingUtil.formatCurrency(adjustment) },
            tax: { text: S25PricingUtil.formatCurrency(tax) },
            total: { text: S25PricingUtil.formatCurrency(adjustment + tax) },
        };

        if (this.combineRelated()) {
            cells.reference = { text: this.pricingStore.eventLocatorMap()[item.eventId!] ?? "" };
        }

        return {
            id: `line-item-amount-${orgId}-${item.eventId}-${item.itemId}-${item.itemType}`,
            name: item.itemName ?? "",
            className: childRowClassName(position),
            cells,
        };
    }

    private adjustmentRows(orgId: number): Table.Row[] {
        const rows: Table.Row[] = (this.adjustmentsByOrg().get(orgId) ?? []).map((adjustment) => ({
            id: `adjustment-${orgId}-${adjustment.itemId}`,
            name: adjustment.adjustmentName ?? "Adjustment",
            cells: {
                item: { text: "Adjustment" },
                adjust: {
                    component: S25PricingAdjustmentCellComponent,
                    inputs: { adjustment, orgId, readOnly: this.readOnly() },
                },
                total: { text: S25PricingUtil.formatCurrency(adjustment.totalCharge ?? 0) },
            },
        }));

        // An org with nothing adjusted still gets a row, which reads as $0.00 when the invoice is
        // locked and offers the Add button when it is not.
        if (this.canEdit() || !rows.length) {
            const empty = !rows.length;
            rows.push({
                id: `adjustment-add-${orgId}`,
                name: this.canEdit() ? "Add Adjustment" : "Adjustment",
                cells: {
                    ...(empty && {
                        item: { text: "Adjustment" },
                        total: { text: S25PricingUtil.formatCurrency(0) },
                    }),
                    adjust: {
                        component: S25PricingAdjustmentCellComponent,
                        inputs: { orgId, readOnly: this.readOnly() },
                    },
                },
            });
        }

        return rows;
    }

    private totalRow(
        orgId: number,
        totals: { listPrice: number; netAdjustments: number; tax: number; grandTotal: number },
    ): Table.Row {
        return {
            id: `total-${orgId}`,
            name: "Total",
            cells: {
                item: { text: "Total" },
                listPrice: { text: S25PricingUtil.formatCurrency(totals.listPrice) },
                adjust: { text: S25PricingUtil.formatCurrency(totals.netAdjustments) },
                price: { text: S25PricingUtil.formatCurrency(totals.netAdjustments + totals.listPrice) },
                tax: { text: S25PricingUtil.formatCurrency(totals.tax) },
                total: { text: S25PricingUtil.formatCurrency(totals.grandTotal) },
            },
        };
    }

    getLineItemUUID(item: LineItem) {
        return `${item.eventId}-${item.itemId}-${item.itemType}-${item.chargeToId}-${item.profileId}`;
    }

    updateTableCellStatus(type: "loading" | "error", key: string, value: boolean | string) {
        if (type === "loading")
            this.statusObj.update((prev) => ({ ...prev, [key]: { ...prev[key], loading: <boolean>value } }));
        else this.statusObj.update((prev) => ({ ...prev, [key]: { ...prev[key], error: <string>value } }));
    }

    /**
     * A move only pauses for input when it puts line item adjustments in play, or when it takes the
     * item off the invoice outright and needs the removal confirmed.
     */
    async onInvoiceToChange(
        selected: string | number | undefined,
        item: InvoiceMove.MovableItem,
        rowKey: string,
        label: string,
        eventId: number | undefined,
    ) {
        const source = this.moveSource(item, eventId);
        if (!source || !selected) return;

        // The new invoice takes the item over once the form has created it.
        if (selected === "new") {
            this.pricingStore.openInvoiceForm({
                mode: "create",
                move: { item, sourceInvoiceId: source.id },
                preselect: item.rsrvId ? { rsrvIds: [item.rsrvId] } : { requirementIds: [item.requirementId!] },
            });
            return;
        }

        if (selected === "remove") {
            this.pendingMove.set({ kind: "remove", item, label, rowKey, source });
            this.moveModal()?.open();
            return;
        }

        const targetId = Number(selected);
        if (!Number.isFinite(targetId)) return;

        // Every event's default set reads as the one Standard Invoice, so a row sent there goes to
        // the set for its own event - the only one that can bill it.
        const chosen = this.pricingStore.invoiceById().get(targetId);
        const target = isDefaultSet(chosen) ? (this.defaultSetForEvent(eventId) ?? chosen) : chosen;
        if (!target || target.id === source.id) return;

        const adjustedItems = InvoiceMove.adjustmentsInPlay({ item, source, target });
        if (!adjustedItems.length) return this.runMove({ item, rowKey, source, target });

        this.pendingMove.set({ kind: "move", item, label, rowKey, source, target, adjustedItems });
        this.moveModal()?.open();
    }

    @Bind
    async confirmMove() {
        const pending = this.pendingMove();
        if (!pending) return;

        // Closing the modal destroys the allocation form, so take its payload first.
        const adjustments = pending.kind === "move" ? (this.allocationForm()?.adjustmentPayload() ?? []) : [];
        this.moveModal()?.close();
        this.pendingMove.set(undefined);

        await this.runMove({
            item: pending.item,
            rowKey: pending.rowKey,
            source: pending.source,
            target: pending.kind === "move" ? pending.target : undefined,
            adjustments,
        });
    }

    cancelMove() {
        this.moveModal()?.close();
        this.pendingMove.set(undefined);
    }

    private async runMove(args: {
        item: InvoiceMove.MovableItem;
        rowKey: string;
        source: Invoice;
        target?: Invoice;
        adjustments?: LineItem[];
    }) {
        this.setMoveStatus(args.rowKey, { loading: true, error: "" });

        const { error } = await this.pricingStore.moveInvoiceItem({
            item: args.item,
            sourceInvoiceId: args.source.id,
            targetInvoiceId: args.target?.id,
            adjustments: args.adjustments,
        });

        this.setMoveStatus(args.rowKey, { loading: false, error: error ?? "" });
        if (error) return;

        this.moveMessage.set(
            args.target
                ? `Moved to ${invoiceDisplayName(args.target) || "the selected invoice"}`
                : `Removed from ${invoiceDisplayName(args.source) || "this invoice"}`,
        );
    }

    private setMoveStatus(rowKey: string, status: { loading: boolean; error: string }) {
        this.moveStatus.update((prev) => ({ ...prev, [rowKey]: status }));
    }

    // The select reports its choice as a string, and the row it came from carries a display name and
    // computed amounts that have no business in a write, so the request is rebuilt from scratch.
    async onOrgChange(item: LineItem, selected: string | number | undefined, uuid: string) {
        const invoiceId = this.owningInvoiceId(item);
        const chargeToId = Number(selected);
        if (!chargeToId || !Number.isFinite(chargeToId) || S25Util.isUndefined(invoiceId)) return;

        this.updateTableCellStatus("error", uuid, "");
        this.updateTableCellStatus("loading", uuid, true);

        const { error } = await this.pricingStore.updateInvoice(
            {
                lineItems: [
                    {
                        billId: invoiceId,
                        itemId: item.itemId,
                        itemType: item.itemType,
                        profileId: item.profileId,
                        chargeToId,
                    },
                ],
            },
            invoiceId,
        );
        if (error) {
            this.updateTableCellStatus("error", uuid, S25Util.errorText(error, S25Const.defaultErrorMessage));
        }

        this.updateTableCellStatus("loading", uuid, false);
    }
}
